import { useTranslation } from "@sagot/i18n";
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  type IconName,
} from "@sagot/ui";
import { formatClock, type ReassignTarget } from "./wallboard-model";

export interface ReassignChat {
  id: string;
  title: string;
  /** Seconds the chat has been held (agent board) - absent for a chat still waiting. */
  heldS?: number;
}

/** A long list scrolls inside the menu; the scroller carries tabindex so the scrollable region is keyboard-reachable for assistive tech (the arrow keys move through the items). */
const SCROLL_REGION = { role: "group", tabIndex: 0 } as const;

interface ReassignMenuProps {
  chats: readonly ReassignChat[];
  targets: readonly ReassignTarget[];
  label: string;
  ariaLabel: string;
  icon: IconName;
  pending: boolean;
  /** The menu's name for the person who holds the chats ("Ramon S."), used in the empty message. */
  holder?: string;
  onPick: (chat: ReassignChat, target: ReassignTarget) => void;
  testId: string;
}

function TargetItem({ chat, target, onPick }: { chat: ReassignChat; target: ReassignTarget; onPick: ReassignMenuProps["onPick"] }) {
  const { t } = useTranslation();
  const reason = target.blocked === "limit" ? t("wallboard.board.reassign.noRoom") : target.blocked === "offline" ? t("wallboard.board.reassign.offline") : null;
  return (
    <DropdownMenuItem
      disabled={target.blocked !== null}
      data-testid={`reassign-target-${target.memberId}`}
      onSelect={() => { onPick(chat, target); }}
    >
      <Avatar name={target.name} className="size-6 text-[10px]" />
      <span className="truncate">{target.name}</span>
      {reason ? <span className="ml-auto text-[11px] font-semibold text-fg-muted">{reason}</span> : null}
    </DropdownMenuItem>
  );
}

/**
 * One-click reassign: a menu of the chats to move (the agent's own, or the one waiting chat) with, under each, the people who can take it.
 * People at their limit or offline are listed but cannot be picked, with the reason beside them. Nothing is sent until a person is chosen.
 */
export function ReassignMenu({ chats, targets, label, ariaLabel, icon, pending, holder, onPick, testId }: ReassignMenuProps) {
  const { t } = useTranslation();
  const open = targets.some((target) => target.blocked === null);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" disabled={pending} aria-label={ariaLabel} data-testid={testId}>
          <Icon name={icon} />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        <div {...SCROLL_REGION} aria-label={ariaLabel} className="max-h-80 overflow-y-auto p-1 focus-visible:outline-2 focus-visible:outline-primary">
        {chats.length === 0 ? (
          <p className="px-2.5 py-2 text-sm text-fg-muted">{t("wallboard.board.reassign.noChats", { name: holder ?? "" })}</p>
        ) : null}
        {chats.map((chat, index) => (
          <div key={chat.id} data-testid={`reassign-chat-${chat.id}`}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="normal-case tracking-normal">
              <span className="block truncate text-xs font-bold text-fg">{t("wallboard.board.reassign.forChat", { title: chat.title })}</span>
              {chat.heldS !== undefined ? (
                <span className="block text-[11px] font-medium text-fg-muted">{t("wallboard.board.reassign.chatHeld", { time: formatClock(chat.heldS) })}</span>
              ) : null}
            </DropdownMenuLabel>
            {targets.map((target) => (
              <TargetItem key={target.memberId} chat={chat} target={target} onPick={onPick} />
            ))}
            {!open ? <p className="px-2.5 py-1.5 text-xs text-fg-muted">{t("wallboard.board.reassign.noTargets")}</p> : null}
          </div>
        ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
